import {
  ServiceCategoryMap,
  type ServiceCategory,
  ServiceCategoryKeyList,
  type ServiceCategoryKey,
} from "../../lib";
import { View } from "@/components/ui";
import { cn, debounce } from "@/lib/utils";
import { Typography } from "@/components/shared/typography";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Divider } from "@/components/shared/divider";
import { useWindowDimensions } from "@/components/hooks/use-window-dimension";
import type { BetaServiceName } from "@/types/service";
import { sendAnalyticData } from "@/lib/monitoring/analytics";
import { ServiceLearnMoreDialog } from "../service-learn-more-dialog";
import { AllServicesEmptyState } from "./empty-state";
import { ServiceCategoryGrid } from "./service-category-grid";
import {
  popularServiceItems,
  otherServiceItems,
  betaServiceItems,
} from "../../lib";

export function AllServicesContent({ query = "" }: { query?: string }) {
  const [currentCate, setCurrentCate] =
    useState<keyof ServiceCategory>("Popular");
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const contentRefs = useRef<Record<string, HTMLElement | null>>({});
  const { width, isMobile } = useWindowDimensions();
  const isNarrow = width <= 375;

  const [showBetaServiceDialog, setShowBetaServiceDialog] = useState<{
    open: boolean;
    url: string;
  }>({
    open: false,
    url: "",
  });

  const matchesQuery = useCallback(
    (name: string) => name.toLowerCase().includes(query.toLowerCase()),
    [query],
  );

  const filteredCategories = useMemo(() => {
    if (!query) return ServiceCategoryKeyList;
    const allItems = [
      ...popularServiceItems(),
      ...otherServiceItems(),
      ...betaServiceItems(),
    ];
    return ServiceCategoryKeyList.filter((category) => {
      return allItems.some(
        (item) =>
          item.categories.includes(category) && matchesQuery(item.title),
      );
    });
  }, [query, matchesQuery]);

  useEffect(() => {
    if (
      filteredCategories.length > 0 &&
      !filteredCategories.includes(currentCate)
    ) {
      setCurrentCate(filteredCategories[0]);
    }
  }, [filteredCategories, currentCate]);

  const onMenuClick = (category: ServiceCategoryKey) => {
    if (!ServiceCategoryKeyList.includes(category)) return;
    setCurrentCate(category);
    const element = contentRefs.current[category];
    if (element && scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const containerTop = container.getBoundingClientRect().top;
      const elementTop = element.getBoundingClientRect().top;
      const offset = elementTop - containerTop;

      container.scrollTo({
        top: offset + container.scrollTop - 20,
        behavior: "smooth",
      });
    }
  };

  const onClickBetaService = (name: BetaServiceName, link: string) => {
    sendAnalyticData({
      name: "view_beta_service",
      values: { service_type: name },
    });
    setShowBetaServiceDialog({
      open: true,
      url: link,
    });
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const containerRect = container.getBoundingClientRect();
      const containerTop = containerRect.top;
      let currentVisibleCate: keyof ServiceCategory | null = null;
      filteredCategories.forEach((category) => {
        const element = contentRefs.current[category];
        if (element) {
          const elementRect = element.getBoundingClientRect();
          const elementTop = elementRect.top - containerTop;

          if (elementTop < 130 && elementTop > -10) {
            currentVisibleCate = category;
          }
        }
      });

      if (currentVisibleCate && currentVisibleCate !== currentCate) {
        setCurrentCate(currentVisibleCate);
      }
    };

    const debounceHandleScroll = debounce(handleScroll, 50);

    container.addEventListener("scroll", debounceHandleScroll);
    return () => container.removeEventListener("scroll", debounceHandleScroll);
  }, [currentCate, filteredCategories]);

  const isEmpty = filteredCategories.length === 0;

  return (
    <View
      className={cn(
        "flex flex-row overflow-hidden",
        isMobile ? "h-full" : "h-[680px]",
      )}
    >
      {isEmpty ? (
        <AllServicesEmptyState query={query} />
      ) : (
        <>
          {/* menu */}
          <View className="w-40 shrink-0 grow-0 rounded-r-2xl bg-background-intermediate">
            {filteredCategories.map((category) => (
              <Typography
                variant="body-md"
                color="foreground-intermediate"
                className={cn(
                  "cursor-pointer border-b bg-background-intermediate p-4 hover:bg-background",
                  {
                    "native:bg-white bg-background text-primary":
                      currentCate === category,
                  },
                )}
                key={category}
                onClick={() => onMenuClick(category)}
              >
                {ServiceCategoryMap[category]}
              </Typography>
            ))}
          </View>
          {/* content */}
          <div className="flex-1 overflow-y-auto p-2" ref={scrollContainerRef}>
            {filteredCategories.map((category, index) => {
              const isLastItem = index === filteredCategories.length - 1;
              return (
                <div
                  key={category}
                  className={cn(
                    isLastItem &&
                      (isMobile ? "h-[calc(100vh-100px)]" : "h-[400px]"),
                    "pb-8",
                  )}
                  ref={(el) => {
                    contentRefs.current[category] = el;
                  }}
                >
                  <Divider className="pb-4">
                    {ServiceCategoryMap[category]}
                  </Divider>
                  <ServiceCategoryGrid
                    category={category}
                    matchesQuery={matchesQuery}
                    isNarrow={isNarrow}
                    onClickBetaService={onClickBetaService}
                  />
                </div>
              );
            })}
          </div>
        </>
      )}
      <ServiceLearnMoreDialog
        open={showBetaServiceDialog.open}
        handleClose={() => setShowBetaServiceDialog({ open: false, url: "" })}
        url={showBetaServiceDialog.url}
      />
    </View>
  );
}
